import { push } from '@lagunovsky/redux-react-router'
import React, { useEffect, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { getStationArrowNav } from 'utils/ActionUtils'
import useActions from 'utils/customHook/useActions'
import useTitle from 'utils/customHook/useTitle'
import CatchmentForm from '../CatchmentForm'
import CatchmentPointsTable from '../CatchmentPointsTable'
import { Stack, Grid } from '@mui/material'
import CatchmentFollowUpPluvio from './pluviometry/CatchmentFollowUpPluvio'
import { CardFollowUpNitrates } from 'quality/components/qualityComponents/FollowUpNitrates'
import useApplicationSetting, { listStringParser } from 'utils/customHook/useApplicationSetting'
import { getBeginingOfTheYear, getEndOfTheYear } from 'utils/DateUtil'
import { CardFollowUpPesticides } from 'quality/components/qualityComponents/FollowUpPesticides'
import useSandreList from 'utils/customHook/useSandreList'
import { SANDRE } from 'referencial/constants/ReferencialConstants'
import CatchmentFollowUpHydro from './hydrometry/CatchmentFollowUpHydro'
import CatchmentFollowUpPiezo from './piezometry/CatchmentFollowUpPiezo'
import QualityAction from 'quality/actions/QualityAction'
import useStartPeriod from 'catchment/utils/useStartPeriod'
import useEndPeriod from 'catchment/utils/useEndPeriod'
import { THRESHOLD } from 'quality/constants/QualityConstants'
import { compact, uniq } from 'lodash'
import sandre from 'assets/pictures/logos/sandre.png'
import mtes from 'assets/pictures/logos/mtes.png'
import { PICTURE } from 'components/file/constant/FileConstants'
import useBoolean from 'utils/customHook/useBoolean'
import ParameterAction from 'referencial/components/parameter/actions/ParameterAction'
import CatchmentMapDashboardPanel from './CatchmentMapDashboardPanel'
import { CATCHMENT_IRRELEVANT_PESTICIDES, CATCHMENT_LIST_SUM_PESTICIDES, CATCHMENT_PESTICIDES } from 'catchment/constants/CatchmentConstant'

const CatchmentDashboardApp = () => {
    const dispatch = useDispatch()

    const {
        catchment,
        catchments,
        pluviometers,
        qualitometers,
        piezometers,
        hydrometricStations,
        pictures,
        thresholds,
        qualityThresholds,
    } = useSelector(store => ({
        catchment: store.CatchmentReducer.catchment,
        catchments: store.CatchmentReducer.catchments,
        pluviometers: store.PluviometryReducer.pluviometers,
        qualitometers: store.QualityReducer.qualitometers,
        piezometers: store.PiezometryReducer.piezometersLight,
        hydrometricStations: store.HydrometryReducer.hydrometricStations,
        pictures: store.StationReducer.pictures,
        thresholds: store.QualityReducer.thresholds,
        qualityThresholds: store.QualityReducer.qualityThresholds,
    }), shallowEqual)

    const startPeriod = useStartPeriod(getBeginingOfTheYear)
    const endPeriod = useEndPeriod(getEndOfTheYear)
    const nitratesThreshold = useApplicationSetting('CATCHMENT_NITRATES_THRESHOLD', setting => setting ? parseFloat(setting) : undefined)
    const excludeProducer = useApplicationSetting('CATCHMENT_PRODUCERS_EXCLUDED', setting => setting?.split(',').filter(c => !!c).map(id => parseInt(id)) || [])
    const pesticideThreshold1 = useApplicationSetting('CATCHMENT_PESTICIDE_THRESHOLD_1', setting => setting ? parseFloat(setting) : undefined)
    const pesticideThreshold2 = useApplicationSetting('CATCHMENT_PESTICIDE_THRESHOLD_2', setting => setting ? parseFloat(setting) : undefined)
    const specificThreshold = useApplicationSetting('CATCHMENT_SPECIFIC_THRESHOLD')

    const relevantPesticides = useApplicationSetting(CATCHMENT_PESTICIDES, listStringParser)
    const irrelevantPesticides = useApplicationSetting(CATCHMENT_IRRELEVANT_PESTICIDES, listStringParser)
    const pesticides = useMemo(() => uniq([...relevantPesticides, ...irrelevantPesticides]), [relevantPesticides, irrelevantPesticides])
    const listSumPesticides = useApplicationSetting(CATCHMENT_LIST_SUM_PESTICIDES, listStringParser)
    const agencies = useSandreList(SANDRE.AGENCES)

    const thresholdList = qualityThresholds.find(t => t.thresholdCode === specificThreshold && t.thresholdType === THRESHOLD.PC)?.thresholds ?? []

    const {
        value: isLoaded,
        setTrue: onLoaded,
    } = useBoolean(false)

    useEffect(() => {
        dispatch(QualityAction.fetchThresholds())
        dispatch(ParameterAction.fetchParameterGroups())
        dispatch(ParameterAction.fetchParameterGroupLinks())
    }, [dispatch])

    useEffect(() => {
        if (specificThreshold && thresholds.length) {
            const parseId = parseInt(specificThreshold)
            const th = thresholds.find(t => t.code === parseId)
            if (th) {
                dispatch(QualityAction.fetchQualityThresholds([{ code: th.code, thresholdType: th.thresholdType }]))
            }
        }
    }, [dispatch, specificThreshold, thresholds])

    const qualitometersLinked = useMemo(() => {
        return catchment.catchmentPoints?.map(point => {
            return qualitometers.find(q => q.code === point.codeWithoutDesignation)
        }).filter(q => !!q) || []
    }, [catchment.catchmentPoints, qualitometers])

    const defaultSelectedPoint = useMemo(() => {
        const mainPointFound = catchment.catchmentPoints?.find(({ mainPoint }) => mainPoint)
        const code = mainPointFound?.codeWithoutDesignation
        const firstQualitometerLinked = qualitometersLinked?.[0]
        return qualitometers.find(q => q.code === code) || firstQualitometerLinked
    }, [catchment.catchmentPoints, qualitometers, qualitometersLinked])

    useTitle(() => [{
        title: i18n.catchments,
        href: 'catchment',
    }, {
        title: catchment.code ? `[${catchment.code}] ${catchment.name ?? ''}` : catchment.name || i18n.unknown,
        href: `station/catchment/${catchment.id}`,
    }, {
        title: i18n.dashboard,
        href: `station/catchment/${catchment.id}`,
    }], [catchment])

    useActions(() => {
        const agency = agencies.find(a => a.code === catchment.pond)
        const ref = agency?.mnemonique || agency?.reference
        const mtesLink = ref ? {
            img: mtes,
            href: `http://www.deb.developpement-durable.gouv.fr/telechargements/fiche.php?IG=${ref}_${catchment.code}`,
            label: i18n.mtes,
        } : undefined
        const sandreLink = catchment.sandreCode ? {
            img: sandre,
            href: `https://id.eaufrance.fr/AAC/${catchment.sandreCode}`,
            label: i18n.sandre,
        } : undefined
        return {
            arrowNav: getStationArrowNav('catchment', catchments, catchment.id, s => dispatch(push(`/station/catchment/${s.id}`))),
            links: compact([mtesLink, sandreLink]),
        }
    }, [catchments, catchment, agencies])

    const hydrometricStationsLinked = useMemo(() => {
        return catchment.catchmentPoints?.map(point => {
            return hydrometricStations.find(q => q.code === point.codeWithoutDesignation)
        }).filter(q => !!q) || []
    }, [catchment.catchmentPoints, hydrometricStations])

    const piezometersLinked = useMemo(() => {
        return catchment.catchmentPoints?.map(point => {
            return piezometers.find(q => q.code === point.codeWithoutDesignation)
        }).filter(q => !!q) || []
    }, [catchment.catchmentPoints, piezometers])

    const pluviometersLinked = useMemo(() => {
        return catchment.catchmentPoints?.map(point => {
            return pluviometers.find(q => q.code === point.codeWithoutDesignation)
        }).filter(q => !!q) || []
    }, [catchment.catchmentPoints, pluviometers])

    const qualitoIds = useMemo(() => qualitometersLinked.map(q => q.id), [qualitometersLinked])

    const imgs = useMemo(() => pictures.filter(f => f.fileType === PICTURE), [pictures])

    return (
        <Stack sx={{ paddingBottom: '100' }} spacing={1}>
            { defaultSelectedPoint && (
                <Grid container spacing={1}>
                    <Grid container direction='column' size={9} spacing={1}>
                        <CatchmentForm catchment={catchment} displayChips/>
                        { (imgs?.length > 0 && isLoaded) && (
                            <CatchmentPointsTable catchmentPoints={catchment.catchmentPoints} />
                        )}
                    </Grid>
                    <Grid container direction='column' size={3} spacing={1}>
                        <CatchmentMapDashboardPanel
                            isLoaded={isLoaded}
                            onLoaded={onLoaded}
                            qualitometersLinked={qualitometersLinked}
                            defaultSelectedPoint={defaultSelectedPoint}
                        />
                    </Grid>
                </Grid>
            ) || (
                <CatchmentForm catchment={catchment} displayChips/>
            )}
            { (imgs?.length === 0 || !isLoaded) && (
                <CatchmentPointsTable catchmentPoints={catchment.catchmentPoints} />
            )}
            {
                !!qualitometersLinked.length && (
                    <>
                        <CardFollowUpNitrates
                            stationIds={qualitoIds}
                            startPeriod={startPeriod}
                            endPeriod={endPeriod}
                            producerExcluded={excludeProducer}
                            nitratesThreshold={nitratesThreshold}
                        />
                        <CardFollowUpPesticides
                            stationIds={qualitoIds}
                            startPeriod={startPeriod}
                            endPeriod={endPeriod}
                            producerExcluded={excludeProducer}
                            threshold1={pesticideThreshold1}
                            threshold2={pesticideThreshold2}
                            specificThresholds={thresholdList}
                            pesticides={pesticides}
                            listSumPesticides={listSumPesticides}
                        />
                    </>
                )
            }
            {
                !!pluviometersLinked.length && (
                    <CatchmentFollowUpPluvio
                        pluviometersLinked={pluviometersLinked}
                    />
                )
            }
            {
                !!hydrometricStationsLinked.length && (
                    <CatchmentFollowUpHydro
                        hydrometricStationsLinked={hydrometricStationsLinked}
                    />
                )
            }
            {
                !!piezometersLinked.length && (
                    <CatchmentFollowUpPiezo
                        piezometersLinked={piezometersLinked}
                    />
                )
            }
        </Stack>
    )
}

export default CatchmentDashboardApp